import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import useSectionReveal from '@/hooks/useSectionReveal';
import { projects } from '@/mocks/home';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const OFFSETS = [
  { x: -70, y: 55, r: -1.5 },
  { x: 85, y: 90, r: 1.2 },
  { x: -55, y: 110, r: 1 },
  { x: 70, y: 65, r: -1.4 },
];

export default function ProjectsSection() {
  const ref = useRef<HTMLElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  useSectionReveal(ref);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    const build = (k: number, rot: boolean) => {
      const cards = gsap.utils.toArray<HTMLElement>('.pj-card', grid.current);
      const tl = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: { trigger: grid.current, start: 'top 92%', end: 'center 55%', scrub: 1 },
      });
      cards.forEach((c, i) => {
        const o = OFFSETS[i % 4];
        tl.fromTo(c, {
          x: o.x * k, y: o.y * k, rotate: rot ? o.r : 0, transformPerspective: 1200, rotateY: rot ? o.r * 1.5 : 0,
        }, { x: 0, y: 0, rotate: 0, rotateY: 0, duration: 1 }, i * 0.12);
        const img = c.querySelector('.pj-img');
        if (img) tl.fromTo(img, { yPercent: -6 * k }, { yPercent: 0, duration: 1.1 }, i * 0.12);
      });
    };
    mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => build(1, true));
    mm.add('(max-width: 767px) and (prefers-reduced-motion: no-preference)', () => build(0.25, false));
    return () => mm.revert();
  }, { scope: ref });

  return (
    <section ref={ref} id="projects" className="bg-background-100 px-4 md:px-10 py-20 md:py-32 overflow-x-clip">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p data-reveal className="text-[11px] uppercase tracking-[0.24em] text-primary-600">Selected residential projects</p>
            <h2 data-reveal className="mt-4 font-heading font-light text-3xl md:text-5xl text-foreground-950">Homes, reframed.</h2>
          </div>
          <a data-reveal href="#estimate" className="text-sm text-primary-700 inline-flex items-center gap-2 whitespace-nowrap cursor-pointer">Start your project <i className="ri-arrow-right-line" /></a>
        </div>
        <div ref={grid} className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {projects.map((p, i) => {
            const span = i === 0 || i === 3 ? 'md:col-span-7' : 'md:col-span-5';
            return (
              <article key={p.title} className={`pj-card ${span} group cursor-pointer will-change-transform`}>
                <div className="relative w-full h-[300px] md:h-[460px] overflow-hidden">
                  <div className="pj-img absolute -inset-y-[6%] inset-x-0">
                    <img loading="lazy" decoding="async" src={p.image} alt={p.title} title={`${p.title} window project`} className="w-full h-full object-cover object-top transition-transform duration-[450ms] ease-out group-hover:scale-[1.025]" />
                  </div>
                </div>
                <div data-reveal className="mt-4 flex items-baseline justify-between gap-3 border-t border-background-300/70 pt-4">
                  <div className="flex items-baseline gap-3 min-w-0">
                    <span className="font-heading text-xs text-foreground-500 transition-colors duration-300 group-hover:text-accent-700">0{i + 1}</span>
                    <h3 className="font-heading text-lg md:text-xl text-foreground-950 transition-colors duration-300 group-hover:text-primary-700">{p.title}</h3>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <p className="hidden sm:block text-xs md:text-sm text-foreground-600">{p.meta}</p>
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-700 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-primary-700">
                      <i className="ri-arrow-right-up-line" />
                    </span>
                  </div>
                </div>
                <p data-reveal className="sm:hidden mt-1 text-xs text-foreground-600">{p.meta}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}